import { z } from 'zod';

/**
 * What the leave form accepts, mirroring
 * `backend/src/modules/institute/leaveRequests/leaveRequests.validation.ts`.
 *
 * Shared by the parent and student panels, which file the same request. The
 * server is still the authority — these rules are here so the person writing
 * is told about an empty reason before it leaves their phone, not after
 * (CLAUDE.md §6).
 */

export const LEAVE_TYPES = ['MEDICAL', 'CASUAL', 'FAMILY_EVENT', 'TRAVEL', 'OTHER'] as const;

export type LeaveType = (typeof LEAVE_TYPES)[number];

/** What a school calls each of them, in the order the form offers them. */
export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  MEDICAL: 'Sick leave / medical',
  CASUAL: 'Casual leave',
  FAMILY_EVENT: 'Family function or event',
  TRAVEL: 'Planned travel',
  OTHER: 'Other',
};

export const MAX_REASON_LENGTH = 500;

export const leaveRequestSchema = z
  .object({
    type: z.enum(LEAVE_TYPES, { errorMap: () => ({ message: 'Choose a reason' }) }),
    fromDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the first day'),
    toDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the last day'),
    reason: z
      .string()
      .trim()
      .min(10, 'Tell the school a little more — at least 10 characters')
      .max(MAX_REASON_LENGTH, `Keep this under ${MAX_REASON_LENGTH} characters`),
  })
  // Checked here as well as on the server so the message lands on the field
  // that is wrong rather than in a toast above the form.
  .refine((values) => values.fromDate <= values.toDate, {
    path: ['toDate'],
    message: 'This is before the first day',
  });

export type LeaveRequestForm = z.infer<typeof leaveRequestSchema>;
